import classnames from 'classnames'
import * as React from 'react'

import { Button, ButtonHTMLProps, ButtonProps } from '../button'
import { Delayed } from '../delayed'
import { Icon } from '../icon'
import { PropsWithAs } from '../types'
import { useToolbarContext } from './hooks'
import { IToolbarItem, ToolbarTheme } from './interfaces'

function getButtonThemeSettings({
  theme,
  isActive,
  isStretched,
  isInAction,
}: {
  theme: ToolbarTheme
  isStretched: boolean
  isActive?: boolean
  isInAction?: boolean
}): Omit<ButtonHTMLProps, 'as'> & ButtonProps {
  const includeRedesign = FinvizSettings.hasRedesignEnabled
  switch (theme) {
    case ToolbarTheme.alternative:
      return {
        className: classnames(
          {
            grow: isStretched,
            'h-10 w-10': !isStretched && !includeRedesign,
            'animate-pulse': isInAction,
          },

          includeRedesign && {
            'rounded disabled:bg-white dark:border-transparent disabled:text-gray-300 disabled:hover:text-gray-300 dark:disabled:bg-gray-800 dark:disabled:text-gray-500':
              true,
            'border border-white text-gray-500 dark:text-gray-400 hover:border-gray-50 hover:bg-gray-50 hover:text-gray-900 dark:hover:text-gray-200 dark:hover:border-gray-700 dark:hover:bg-gray-700':
              !isActive,
            'border text-gray-800 dark:border-blue-500 border-blue-400 bg-blue-50 dark:text-white dark:bg-gray-700':
              isActive,
          },

          !includeRedesign && {
            'active:bg-white disabled:bg-white disabled:text-gray-300 disabled:hover:text-gray-300': true,
            'border border-white text-gray-500 hover:border-gray-100 hover:text-gray-900': !isActive,
            'border border-gray-900 text-gray-900': isActive,
          }
        ),
        theme: 'transparent',
        rounding: 'none',
        size: isStretched || includeRedesign ? undefined : 'none',
      }
    default:
      return {
        className: classnames(
          'dark:disabled:bg-gray-800 dark:disabled:text-gray-600 disabled:text-gray-300 disabled:bg-white',
          { 'animate-pulse': isInAction }
        ),
        theme: 'chipTransparent',
        rounding: 'small',
      }
  }
}

export interface ToolbarButtonProps extends Omit<ButtonProps, 'theme'> {
  item: IToolbarItem
  disabledTooltip?: string
  isInAction?: boolean
}

export function ToolbarButton<TagType extends React.ElementType = 'button'>({
  item,
  disabledTooltip,
  isInAction: isInActionProp,
  ...props
}: PropsWithAs<TagType> & ToolbarButtonProps) {
  const { theme, isStretched } = useToolbarContext()
  const isInAction = isInActionProp && item.iconAction !== undefined
  const buttonSettings = getButtonThemeSettings({
    theme,
    isStretched,
    isActive: props.active,
    isInAction: isInAction,
  })

  const getMainIcon = () => (
    <Icon name={(props.active && item.iconActive) || item.icon} width={32} className=" shrink-0" />
  )

  return (
    <Button
      {...buttonSettings}
      {...(props as PropsWithAs<TagType> & ToolbarButtonProps)}
      appearance={props.appearance ?? 'square'}
      title={props.disabled && disabledTooltip ? disabledTooltip : (props.active && item.titleActive) || item.title}
      size={
        buttonSettings.size ??
        (theme === ToolbarTheme.chipTransparent || FinvizSettings.hasRedesignEnabled ? 'medium' : 'large')
      }
    >
      {props.children ??
        (isInAction ? (
          <Delayed delay={100} delayComponent={getMainIcon()}>
            <Icon name={item.iconAction} width={32} className="shrink-0" />
          </Delayed>
        ) : (
          getMainIcon()
        ))}
    </Button>
  )
}
